/**
 * Day Classification Engine (§2, §3, §4, §11)
 *
 * CPR width classification (narrow/normal/wide vs historical SMA),
 * CPR positioning vs the previous session, opening-price analysis and
 * gap classification. Thresholds are fully user-configurable — nothing
 * is hard-coded to a specific symbol's price scale.
 */

import {
  BiasState, CprCore, DayCondition, GapState, OpenLocation, PivotLevels, PositionType,
  PriceVsCpr, WidthClassification, WidthType,
} from "./types";

/**
 * §3 — compare current CPR width against the SMA of HISTORICAL widths
 * (current session excluded; up to `lookback` most recent prior sessions).
 * Returns avgWidth = null when fewer than 3 samples → caller falls back
 * to NORMAL (insufficient data must not scream NARROW/WIDE).
 */
export const classifyWidth = (
  currentWidth: number,
  priorWidths: number[],
  lookback: number,
  narrowThreshold: number,
  wideThreshold: number,
): WidthClassification => {
  const n = Math.max(1, Math.floor(lookback));
  const window = priorWidths.slice(-n);
  if (window.length < 3) {
    return { type: "NORMAL", avgWidth: null, lookback: n, usedSamples: window.length };
  }
  let sum = 0;
  for (const w of window) sum += w;
  const avgWidth = sum / window.length;

  let type: WidthType = "NORMAL";
  if (currentWidth < avgWidth * narrowThreshold) type = "NARROW";
  else if (currentWidth > avgWidth * wideThreshold) type = "WIDE";

  return { type, avgWidth, lookback: n, usedSamples: window.length };
};

/**
 * §4 — today's CPR relative to yesterday's CPR.
 * HIGHER  → current bottom > previous top   (bullish bias potential)
 * LOWER   → current top    < previous bottom (bearish bias potential)
 * else    → OVERLAPPING (neutral / range-oriented)
 */
export const classifyPosition = (cur: CprCore, prev: CprCore): PositionType => {
  if (cur.bottom > prev.top) return "HIGHER CPR";
  if (cur.top < prev.bottom) return "LOWER CPR";
  return "OVERLAPPING CPR";
};

/** §3/§5 — day-condition wording from the width class (no guarantees). */
export const dayConditionFromWidth = (w: WidthType): DayCondition => {
  if (w === "NARROW") return "POTENTIAL EXPANSION";
  if (w === "WIDE") return "POTENTIAL RANGE";
  return "NEUTRAL STRUCTURE";
};

/** §11 — where the session open sits vs CPR / R1 / S1 (most extreme wins). */
export const classifyOpenLocation = (
  open: number,
  cpr: CprCore,
  levels: PivotLevels,
): OpenLocation => {
  if (open > levels.r1) return "OPEN ABOVE R1";
  if (open > cpr.top) return "OPEN ABOVE CPR";
  if (open < levels.s1) return "OPEN BELOW S1";
  if (open < cpr.bottom) return "OPEN BELOW CPR";
  return "OPEN INSIDE CPR";
};

/** §11 — configurable gap classification vs the previous close. */
export const classifyGap = (
  open: number,
  prevClose: number,
  gapThresholdPct: number,
): GapState => {
  if (prevClose === 0) return "FLAT / SMALL GAP";
  const pct = ((open - prevClose) / prevClose) * 100;
  if (pct > gapThresholdPct) return "GAP UP";
  if (pct < -gapThresholdPct) return "GAP DOWN";
  return "FLAT / SMALL GAP";
};

/** Current price vs the CPR zone. */
export const priceVsCpr = (price: number, cpr: CprCore): PriceVsCpr => {
  if (price > cpr.top) return "ABOVE CPR";
  if (price < cpr.bottom) return "BELOW CPR";
  return "INSIDE CPR";
};

/**
 * §5/§17 — market bias by majority vote of the three inputs
 * (CPR position vs previous, price vs CPR, price vs VWAP).
 * A neutral/missing input contributes 0.
 */
export const classifyBias = (
  position: PositionType,
  vsCpr: PriceVsCpr,
  vwapState: "ABOVE" | "BELOW" | "N/A",
): BiasState => {
  let vote = 0;
  if (position === "HIGHER CPR") vote += 1;
  else if (position === "LOWER CPR") vote -= 1;

  if (vsCpr === "ABOVE CPR") vote += 1;
  else if (vsCpr === "BELOW CPR") vote -= 1;

  if (vwapState === "ABOVE") vote += 1;
  else if (vwapState === "BELOW") vote -= 1;

  if (vote >= 1) return "BULLISH";
  if (vote <= -1) return "BEARISH";
  return "NEUTRAL";
};
